import BaseAPI, { recoverFromNotFound, ROUTES, VERSIONS } from './BaseAPI'

const { VERSION_FIVE, VERSION_TWO } = VERSIONS
const { GROUP_ADD_MEMBERS, GROUP_CREATE, GROUP_FIND, GROUP_UPDATE } = ROUTES

export default class GroupsAPI extends BaseAPI {
  /**
   * POST /group - create a group
   * @param  {string} options.name - Group name
   * @param  {File} options.avatarFile - Avatar
   * @param  {Boolean} options.replayHistory - Replay History
   * @param  {Array<string>} options.memberIds - Member IDs
   * @param  {string} options.organizationId - Organization ID
   * @return {Promise.<Object,Error>} - A promise with an object representing a group
   */
  async create({
    avatarFile,
    description,
    excludeCreator,
    isPublic = false,
    memberIds = [],
    name,
    organizationId,
    replayHistory = false,
  }) {
    const { version=VERSION_TWO } = super.getVersion(GROUP_CREATE)
    const headers = {}
    const data = {
      'is_public': isPublic,
      'members': memberIds.join(','),
      'name': name,
      'replay_history': replayHistory,
    }

    const files = {}
    const query = { 'exclude_creator': excludeCreator }

    if (avatarFile) files['avatar'] = avatarFile
    if (description) data['description'] = description

    if (organizationId) {
      headers['TT-X-Organization-Key'] = organizationId
    }

    const res = await this.httpClient.post('/:version/group', {
      urlParams: { version },
      query,
      headers,
      data,
      files
    })

    if (version === VERSION_FIVE) {
      return res.data
    }

    const avatarUrl = res.getHeader('TT-X-Group-Avatar')
    const id = res.getHeader('TT-X-Group-Token')

    const group = {
      avatarUrl,
      id,
      memberIds,
      name,
      organizationId,
    }

    return group
  }

  /**
   * POST /group/:id - update a group
   * @param  {string} options.name - Group name
   * @param  {File} options.avatarFile - Avatar
   * @param  {Boolean} options.replayHistory - Replay History
   * @param  {Array<string>} options.memberIds - Member IDs
   * @return {Promise.<Object,Error>} - A promise with an object representing a group
   */
  async update(id, {
    name = undefined,
    avatarFile = undefined, // TODO
    replayHistory = undefined,
    memberIds
  }) {
    // TODO validation
    const { version=VERSION_TWO } = super.getVersion(GROUP_UPDATE)
    const data = {}
    const files = {}

    if (typeof name !== 'undefined') data['name'] = name
    if (typeof memberIds !== 'undefined') data['members'] = memberIds.join(',')
    if (typeof replayHistory !== 'undefined') data['replay_history'] = replayHistory
    if (avatarFile) files['avatar'] = avatarFile

    const res = await this.httpClient.post('/:version/group/:id', {
      urlParams: { id, version },
      data,
      files
    })

    const avatarUrl = res.getHeader('TT-X-Group-Avatar')

    return {
      id,
      avatarUrl
    }
  }

  @recoverFromNotFound()
  async find(id: string) {
    const { version=VERSION_TWO } = super.getVersion(GROUP_FIND)
    const res = await this.httpClient.get('/:version/group/:id', {
      urlParams: { id, version },
      query: { 'render_metadata': 'true' },
    })
    return res.data
  }

  @recoverFromNotFound()
  async findAllMemberIds(id: string) {
    const res = await this.httpClient.get('/v2/group/:id/members', {
      urlParams: { id }
    })
    return res.data
  }

  @recoverFromNotFound()
  async destroy(id: string) {
    await this.httpClient.del('/v2/group/:id', { urlParams: { id } })
    return true
  }

  async addMembers(id: string, memberIds: string[]) {
    const { version=VERSION_TWO } = super.getVersion(GROUP_ADD_MEMBERS)
    const res = await this.httpClient.post('/:version/group/:id/members', {
      urlParams: { id, version },
      data: {
        members: memberIds
      }
    })

    return res.data
  }

  async removeMembers(id: string, memberIds: string[]) {
    await this.httpClient.post('/v2/group/:id/members/remove', {
      urlParams: { id },
      data: {
        members: memberIds
      }
    })
  }
}
